import {Injectable} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  addDays, addHours, addMinutes, addMonths, addSeconds, addWeeks, addYears,
  compareDesc,
  differenceInCalendarDays, differenceInCalendarMonths, differenceInDays,
  differenceInHours, differenceInMilliseconds, differenceInMinutes, differenceInMonths,
  differenceInWeeks, differenceInYears,
  distanceInWords, distanceInWordsToNow,
  endOfDay, endOfMonth, endOfQuarter, endOfWeek,
  format as dateFnsFormat,
  getDate, getDay, getDaysInMonth, getISOWeek,
  differenceInSeconds,
  isAfter, isBefore, isDate, isEqual, isFuture, isPast, isSameDay, isThisISOWeek, isThisMonth, isThisYear, isToday, isTomorrow, isValid, isWithinRange, isYesterday,
  lastDayOfMonth,
  max,
  parse,
  setISOWeek, setSeconds,
  startOfDay, startOfMonth, startOfQuarter, startOfWeek, startOfYear,
  subDays, subMinutes, subMonths, subWeeks,
} from "date-fns";
import { BehaviorSubject, distinctUntilChanged, map, Observable, Subject } from "rxjs";

export type DateInput = Date | string | number;

export class DateFormat {
  public format: string;
  public dateOrder: string;
  public display: string;
  public description: string;
  public calendarFormat: string;
  public dateFnFormat: string;
}

export class TimeFormat {
  public format: string;
  public segment: string;
  public dateFnFormat: string;
  public display?: string;
}

export class SymbolPosition {
  public position: string;
  public value: string;
}

export class DecimalPosition {
  public precision: boolean;
  public toFixed: number;
  public value: string;
}

export class DistanceUnit {
  public unit: string;
  public value: 'km' | 'mi';
  public display: string;
}

export enum DATE_TIME_CONSTANTS {
  DATE="DATE", TIME="TIME", DATE_TIME="DATE_TIME", PICKER_FORMAT="PICKER_FORMAT", DISPLAY_TIME="DISPLAY_TIME"
}

@Injectable({
  providedIn: 'root'
})
export class DateTimeFormatsService {

  public updateSelectedTime = new Subject<any>();
  updateSelectedTime$ = this.updateSelectedTime.asObservable();
  public updateSelectedNewTimeTimeOffRequest = new Subject<any>();
  updateSelectedNewTimeTimeOffRequest$ = this.updateSelectedNewTimeTimeOffRequest.asObservable();
  public updateSelectedTimeTimeOffRequest = new Subject<any>();
  updateSelectedTimeTimeOffRequest$ = this.updateSelectedTimeTimeOffRequest.asObservable();
  public updateSelectedTimeMessage = new Subject<any>();
  updateSelectedTimeMessage$ = this.updateSelectedTimeMessage.asObservable();
  public updateSelectedTimeSurgeryOrder = new Subject<any>();
  updateSelectedTimeSurgeryOrder$ = this.updateSelectedTimeSurgeryOrder.asObservable();
  public updateSelectedTimeEvent = new Subject<any>();
  updateSelectedTimeEvent$ = this.updateSelectedTimeEvent.asObservable();
  public updateSelectedTimeConfiguredForm = new Subject<any>();
  updateSelectedTimeConfiguredForm$ = this.updateSelectedTimeConfiguredForm.asObservable();
  public updateSelectedTimeConfiguredSectionForm = new Subject<any>();
  updateSelectedTimeConfiguredSectionForm$ = this.updateSelectedTimeConfiguredSectionForm.asObservable();
  public updateSelectedTimeAllocationOrder = new Subject<any>();
  updateSelectedTimeAllocationOrder$ = this.updateSelectedTimeAllocationOrder.asObservable();
  public updateSelectedAppointment = new Subject<any>();
  updateSelectedAppointment$ = this.updateSelectedAppointment.asObservable();
  
  public isNewActivity: boolean = false;
  limitedMinTimeValue: string = "00:00 AM";
  limitedMinTimeValueForMessage: string = "00:00 AM";
  public updateLimitedMinTime = new Subject<any>();
  updateLimitedMinTime$ = this.updateLimitedMinTime.asObservable();

  private currentLocale: object = {};

  setCurrentLocale(locale: object): void {
    this.currentLocale = locale ?? {};
  }

  public dateFormat = new Subject<string>();
  public timeFormat = new Subject<string>();
  public symbolPos = new Subject<string>();
  public decimalPos = new Subject<string>();
  public readonly distanceUnit: Observable<string>;
  public allFormats: DateFormat[] = [
    {
      "format": "1",
      "dateOrder": "MM/dd/yyyy",
      "display": "MM/DD/YY",
      "calendarFormat": "mm/dd/yy",
      "dateFnFormat": "MM/DD/YYYY",
      "description": "Month-Day-Year with leading zeros (02/17/2009)"
    },
    {
      "format": "2",
      "dateOrder": "dd/MM/yyyy",
      "display": "DD/MM/YY",
      "calendarFormat": "dd/mm/yy",
      "dateFnFormat": "DD/MM/YYYY",
      "description": "Day-Month-Year with leading zeros (17/02/2009)"
    },
    {
      "format": "3",
      "dateOrder": "yyyy/MM/dd",
      "display": "YY/MM/DD",
      "calendarFormat": "yy/mm/dd",
      "dateFnFormat": "YYYY/MM/DD",
      "description": "Year-Month-Day with leading zeros (2009/02/17)"
    },
    {
      "format": "4",
      "dateOrder": "MMMM d, yyyy",
      "display": "Month D, Yr",
      "calendarFormat": "MM dd, yy",
      "dateFnFormat": "MMMM d, YYYY",
      "description": "Month name-Day-Year with no leading zeros \n(February 17, 2009)"
    },
    {
      "format": "5",
      "dateOrder": "M/d/yyyy",
      "display": "M/D/YY",
      "calendarFormat": "m/d/yy",
      "dateFnFormat": "M/d/YYYY",
      "description": "Month-Day-Year with no leading zeros (2/17/2009)"
    },
    {
      "format": "6",
      "dateOrder": "d/M/yyyy",
      "display": "D/M/YY",
      "calendarFormat": "d/m/yy",
      "dateFnFormat": "d/M/YYYY",
      "description": "Day-Month-Year with no leading zeros (17/2/2009)"
    },
    {
      "format": "7",
      "dateOrder": "yyyy/M/d",
      "display": "YY/M/D",
      "calendarFormat": "yy/m/d",
      "dateFnFormat": "YYYY/M/d",
      "description": "Year-Month-Day with no leading zeros (2009/2/17)"
    },
    /*{
      "format": "8",
      "dateOrder": " M/d/yyyy",
      "display": "bM/bD/YY",
      "description": "Month-Day-Year with spaces instead of leading zeros \n( 2/17/2009)"
    },
    {
      "format": "9",
      "dateOrder": "d/ M/yyyy",
      "display": "MM/DD/YY",
      "description": "Day-Month-Year with spaces instead of leading zeros\n(17/ 2/2009)"
    },*/
    {
      "format": "default",
      "dateOrder": "MMM dd, yyyy",
      "display": "MMM DD, YYYY",
      "calendarFormat": "M dd, yy",
      "dateFnFormat": "MMM dd, YYYY",
      "description": "Month-Day-Year with no separators (Dec 27, 2019)"
    },
    /*{
      "format": "A",
      "dateOrder": "yyyy/ M/dd",
      "display": "MM/DD/YY",
      "description": "Year-Month-Day with spaces instead of leading zeros (2009/ 2/17)"
    },
    {
      "format": "B",
      "dateOrder": "MMddyyyy",
      "display": "MMDDYY",
      "description": "Month-Day-Year with no separators (02172009)"
    },
    {
      "format": "C",
      "dateOrder": "ddMMyyyy",
      "display": "DDMMYY",
      "description": "Day-Month-Year with no separators (17022009)"
    },
    {
      "format": "D",
      "dateOrder": "yyyyMMdd",
      "display": "YYMMDD",
      "description": "Year-Month-Day with no separators (20090217)"
    },
    {
      "format": "E",
      "dateOrder": "MMMddyyyy",
      "display": "MonDDYY",
      "description": "Month abbreviation-Day-Year with leading zeros (Feb172009)"
    },
    {
      "format": "F",
      "dateOrder": "ddMMMyyyy",
      "display": "DDMonYY",
      "description": "Day-Month abbreviation-Year with leading zeros (17Feb2009)"
    },
    {
      "format": "G",
      "dateOrder": "yyyyMMMdd",
      "display": "YYMonDD",
      "description": "Year-Month abbreviation-Day with leading zeros (2009Feb17)"
    },*/
    {
      "format": "J",
      "dateOrder": "dd MMMM, yyyy",
      "display": "D Month, Yr",
      "calendarFormat": "dd MM, yy",
      "dateFnFormat": "DD MMMM, YYYY",
      "description": "Day-Month name-Year (17 February, 2009)"
    },
    {
      "format": "K",
      "dateOrder": "yyyy, MMMM dd",
      "display": "Yr, Month D",
      "calendarFormat": "yy, MM dd",
      "dateFnFormat": "YYYY, MMMM DD",
      "description": "Year-Month name-Day (2009, February 17)"
    },
    {
      "format": "L *",
      "dateOrder": "MMM-dd-yyyy",
      "display": "Mon-DD-YYYY",
      "calendarFormat": "M-dd-yy",
      "dateFnFormat": "MMM-DD-YYYY",
      "description": "Month abbreviation, Day with leading zeros, Year\n(Feb 17, 2009)"
    },
    {
      "format": "M *",
      "dateOrder": "dd-MMM-yyyy",
      "display": "DD-Mon-YYYY",
      "calendarFormat": "dd-M-yy",
      "dateFnFormat": "DD-MMM-YYYY",
      "description": "Day with leading zeros, Month abbreviation, Year\n17 Feb, 2009."
    },
    {
      "format": "N",
      "dateOrder": "yyyy-MMM-dd",
      "display": "YYYYY-Mon-DD",
      "calendarFormat": "yy-M-dd",
      "dateFnFormat": "YYYY-MMM-DD",
      "description": "Year, Month abbreviation, Day with leading zeros\n(2009, Feb 17) This format defaults to a two-digit year, but can be overridden to have four digits."
    },
    /*{
      "format": "O",
      "dateOrder": "MMM dd, yyyy",
      "display": "Mon DD, YYYY",
      "description": "Month abbreviation, Day with leading zeros, Year\n(Feb 17, 2014)"
    },*/
    {
      "format": "P",
      "dateOrder": "dd MMM, yyyy",
      "display": "DD Mon, YYYY",
      "calendarFormat": "dd M, yy",
      "dateFnFormat": "DD MMM, YYYY",
      "description": "Day with leading zeros, Month abbreviation, Year\n(17 Feb, 2014)"
    },
    {
      "format": "Q",
      "dateOrder": "yyyy, MMM dd",
      "display": "YYYY, Mon DD",
      "calendarFormat": "yy, M dd",
      "dateFnFormat": "YYYY, MMM DD",
      "description": "Year, Month abbreviation, Day with leading zeros\n(2014, Feb 17)"
    },
    {
      "format": "R",
      "dateOrder": "dd.MM.yyyy",
      "display": "DD.MM.YYYY",
      "calendarFormat": "dd.mm.yy",
      "dateFnFormat": "DD.MM.YYYY",
      "description": "Day-Month-Year with leading zeros and dot separators (17.02.2009)"
    }
  ];
  public timeFormatSegments: TimeFormat[] = [
    {
      format: "12_HOUR",
      display: "12-Hour",
      segment: "h:mm aa",
      dateFnFormat: 'h:mm A',
    },
    {
      format: "24_HOUR",
      display: "24-Hour",
      segment: "HH:mm",
      dateFnFormat: "HH:mm"
    }
  ];
  public symbolsPos: SymbolPosition[] = [{position: "left", value: "$0.00"}, {position: "right", value: "0.00$"}];
  public decimalsPos: DecimalPosition[] = [{precision: false, toFixed: 0, value: "0"}, {precision: true, toFixed: 1, value: "0.0"}, {precision: true, toFixed: 2, value: "0.00"}, {precision: true, toFixed: 3, value: "0.000"}, {precision: true, toFixed: 4, value: "0.0000"}];
  public distanceUnits: DistanceUnit[] = [
    {unit: "kilometer", value: "km", display: "Kilometer"},
    {unit: "mile", value: "mi", display: "Mile"}
  ];

  public selected: DateFormat;
  public timeFormatSelected: TimeFormat;
  public symbolPosSelected: SymbolPosition;
  public decimalPosSelected: DecimalPosition;
  public readonly distanceUnitSelected$: BehaviorSubject<DistanceUnit>;
  public get distanceUnitSelected() {
    return this.distanceUnitSelected$.getValue();
  }
  private defaultFormat: DateFormat = {
    "format": "default",
    "dateOrder": "MMM dd, yyyy",
    "display": "MMM DD, YYYY",
    "calendarFormat": "M dd, yy",
    "dateFnFormat": "MMM DD, YYYY",
    "description": "Month-Day-Year with no separators (Dec 27, 2019)"
  };
  private defaultTimeFormat: TimeFormat = { format: "12_HOUR", segment: "h:mm aa", dateFnFormat: 'h:mm A' };
  private defaultSymbolPos: SymbolPosition = {position: "left", value: "$0.00"};
  private defaultDecimalPos: DecimalPosition = {precision: true, toFixed: 2, value: "0.00"};
  private readonly defaultDistanceUnit: DistanceUnit = {unit: "kilometer", value: "km", display: "Kilometer"};
  
  public isDateFormatSetViaApp:boolean =  false;

  constructor(
    public translate:TranslateService,
  ) {
    this.selected = this.defaultFormat;
    this.timeFormatSelected = this.defaultTimeFormat;
    this.symbolPosSelected = this.defaultSymbolPos;
    this.decimalPosSelected = this.defaultDecimalPos;
    this.distanceUnitSelected$ = new BehaviorSubject<DistanceUnit>(this.defaultDistanceUnit);
    this.distanceUnit = this.distanceUnitSelected$
      .pipe(
        map((distanceUnit) => distanceUnit.display),
        distinctUntilChanged()
      );
    this.getDateTimeCurrencySettings();
  }

  getDateTimeCurrencySettings() {
    let selectedDateFormat = localStorage.getItem('selectedDateFormat') ? JSON.parse(localStorage.getItem('selectedDateFormat')) : this.defaultFormat;
    let selectedTimeFormat = localStorage.getItem('selectedTimeFormat') ? JSON.parse(localStorage.getItem('selectedTimeFormat')) : this.defaultTimeFormat;
    let selectedSymbolPosition = localStorage.getItem('selectedSymbolPosition') ? JSON.parse(localStorage.getItem('selectedSymbolPosition')) : this.defaultSymbolPos;
    let selectedDecimalPosition = localStorage.getItem('selectedDecimalPosition') ? JSON.parse(localStorage.getItem('selectedDecimalPosition')) : this.defaultDecimalPos;
    let selectedDistanceUnit = localStorage.getItem('selectedDistanceUnit') ? JSON.parse(localStorage.getItem('selectedDistanceUnit')) : this.defaultDistanceUnit;
    this.setDateFormat(selectedDateFormat);
    this.setTimeFormat(selectedTimeFormat);
    this.setSymbolPos(selectedSymbolPosition);
    this.setDecimalPos(selectedDecimalPosition);
    this.setDistanceUnit(selectedDistanceUnit);
  }


  dateFormats(): DateFormat[] {
    return this.allFormats;
  }

  timeFormats(): TimeFormat[] {
    return this.timeFormatSegments;
  }

  symbolPositions(): SymbolPosition[] {
    return this.symbolsPos;
  }

  decimalPositions(): DecimalPosition[] {
    return this.decimalsPos;
  }

  distanceUnitsList(): DistanceUnit[] {
    return this.distanceUnits;
  }

  public get selectedFormat(): DateFormat {
    return this.selected;
  }

  public setDateFormat(value: DateFormat) {
    this.selected = value;
  }

  public get selectedTimeFormat(): TimeFormat {
    return this.timeFormatSelected;
  }

  public setTimeFormat(value: TimeFormat) {
    this.timeFormatSelected = value;
  }

  public get selectedSymbolPos(): SymbolPosition {
    return this.symbolPosSelected;
  }

  public setSymbolPos(value: SymbolPosition) {
    this.symbolPosSelected = value;
  }

  public get selectedDecimalPos(): DecimalPosition {
    return this.decimalPosSelected;
  }

  public setDecimalPos(value: DecimalPosition) {
    this.decimalPosSelected = value;
  }

  public get selectedDistanceUnit(): DistanceUnit {
    return this.distanceUnitSelected$.getValue();
  }

  public setDistanceUnit(value: DistanceUnit) {
    this.distanceUnitSelected$.next(value);
  }

  public get date(): string {
    return this.selected.dateOrder;
  }

  public get dateToUpper(): string {
    return this.selected.dateOrder.toUpperCase();
  }

  public get time(): string {
    return this.timeFormatSelected.segment;
  }

  public get timeToUpper(): string {
    return this.timeFormatSelected.format == '12_HOUR' ? 'h:mm A' : 'HH:mm';
  }
  // formatted hour for ngx-material-timepicker
  public get timeToUpperForTimePicker(): number {
    return this.timeFormatSelected.format == '12_HOUR' ? 12 : 24;
  }

  public get dateTimeToUpper(): string {
    return this.dateToUpper + " " + this.timeToUpper;
  }

  public get dateTime(): string {
    return this.date + " " + this.time;
  }

  public get is12HourFormat(): boolean {
    return this.timeFormatSelected.format == '12_HOUR';
  }

  public getFormattedNumber(value:number) :string{
    let str = "0.00";
    if(value){
      str =  value.toFixed(this.selectedDecimalPos.toFixed).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    return str;
  }

  private getSymbolValueObj(value: string) : any {
    let symbol =  value.match(/[^\d,.]/g) ?value.match(/[^\d,.]/g).join('') : '';
    value = value.split('.')[0]+'.00'
    // @ts-ignore
    let val = (value.match(/\d/g).join('') / 100)
    .toFixed(this.selectedDecimalPos.toFixed)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return {value: val, symbol: symbol};
  }

  public formattedCurrency(value: string) : string {
    let obj = this.getSymbolValueObj(value);
    return this.selectedSymbolPos.position == "left"
      ? obj.symbol + obj.value
      : obj.value + obj.symbol;
  }

  getFormattedTimeInterval(startTime: Date | string, endTime: Date | string): string {
    let duration;
    let days: any = differenceInDays(endTime, startTime);
    let timeDifference: Date = new Date((new Date(endTime).setSeconds(0).valueOf() - new Date(startTime).setSeconds(0).valueOf()));
    let minutes: any = timeDifference.getUTCMinutes();
    let hours: any = timeDifference.getUTCHours();
    if (days === 0) {
      days = "";
    } else if (days <= 1) {
      if (hours !=0 || minutes !=0) {
        days = days + " " + this.translate.instant('DAY') + ", ";
      } else {
        days = days + " " + this.translate.instant('DAY');
      }
    } else {
      if (hours !=0 || minutes !=0) {
        days = days + " " + this.translate.instant('DAYS') + ", ";
      } else {
        days = days + " " + this.translate.instant('DAYS');
      }
    }
    if (hours === 0) {
      hours = "";
    } else if (hours <= 1) {
      if (minutes === 0) {
        hours = hours + " " + this.translate.instant('HOUR');
      } else {
        hours = hours + " " + this.translate.instant('HOUR') + ", ";
      }
    }
    else {
      if (minutes === 0) {
        hours = hours + " " + this.translate.instant('HOURS');
      } else {
        hours = hours + " " + this.translate.instant('HOURS') + ", ";
      }
    }
    if (minutes === 0) {
      minutes = "";
    } else {
      minutes = minutes + " " + this.translate.instant('MINUTES');
    }
    duration = days + "" + hours + "" + minutes;
    return duration;
  }

  getFormattedTimeValue(selectedTime: string|Date): string {
    let formattedTime: string;
    let dateTimeValue = new Date(selectedTime);
    if (this.timeToUpperForTimePicker == 24 && dateTimeValue.getHours() == 0) {
      formattedTime = "00:" + dateTimeValue.getMinutes();
    } else formattedTime = new Date(selectedTime).toLocaleTimeString('en-US', { hour12: this.is12HourFormat, hour: '2-digit', minute: '2-digit' });
    return formattedTime;
  }

  formatDateForFetchXML(date){
   return dateFnsFormat(new Date(date), "YYYY-MM-DD")
  }

  formatDateForWithStartTime(date){
   return startOfDay(new Date(date))
  }

  formatDateForWithEndTime(date){
    return endOfDay(new Date(date))
   }

  // --- Date utility methods ---
  // All date manipulation should go through these methods.
  // No component or service should import date-fns or moment directly.

  /** Safely parse a date value. Returns null if invalid. */
  parseDate(value: string | number | Date): Date | null {
    if (value == null) return null;
    const date = new Date(value);
    return isValid(date) ? date : null;
  }

  /** Check if a value is a valid date */
  isValidDate(value: any): boolean {
    if (value == null) return false;
    return isValid(new Date(value));
  }

  // --- Formatting ---

  /** Format a date using a custom format string (date-fns v1 tokens: YYYY, MM, DD, etc.) */
  formatDate(date: DateInput, formatStr?: string): string {
    const parsed = this.parseDate(date);
    if (!parsed) return '';
    return dateFnsFormat(parsed, formatStr || this.selected.dateOrder, this.currentLocale);
  }

  /** Format using the user's selected date format */
  formatDateForDisplay(date: DateInput): string {
    // dateFnFormat carries date-fns v1 tokens (YYYY/DD); dateOrder carries
    // DatePipe-style tokens (yyyy/dd) which date-fns v1 mis-renders.
    return this.formatDate(date, this.selected.dateFnFormat);
  }

  /** Format using the user's selected date + time format */
  formatDateTimeForDisplay(date: DateInput): string {
    return this.formatDate(date, this.selected.dateFnFormat + ' ' + this.timeFormatSelected.segment);
  }

  /** Format as ISO 8601 for API calls */
  formatDateForAPI(date: DateInput): string {
    return this.formatDate(date, 'YYYY-MM-DDTHH:mm:ss');
  }

  /**
   * Returns a native ISO 8601 string (e.g. "2026-03-30T10:30:00.000Z").
   * Use this wherever moment().format() was previously used for internal
   * computation — NOT for display. Returns '' for invalid/null input.
   */
  toISOString(date: DateInput): string {
    const parsed = this.parseDate(date);
    if (!parsed) return '';
    return parsed.toISOString();
  }

  /** Format using the user's selected time format */
  formatTimeForDisplay(date: DateInput): string {
    return this.formatDate(date, this.timeFormatSelected.segment);
  }

  // --- Comparison ---

  isBeforeDate(date: DateInput, dateToCompare: DateInput): boolean {
    const a = this.parseDate(date);
    const b = this.parseDate(dateToCompare);
    if (!a || !b) return false;
    return isBefore(a, b);
  }

  isAfterDate(date: DateInput, dateToCompare: DateInput): boolean {
    const a = this.parseDate(date);
    const b = this.parseDate(dateToCompare);
    if (!a || !b) return false;
    return isAfter(a, b);
  }

  isSameDayCheck(a: DateInput, b: DateInput): boolean {
    const dateA = this.parseDate(a);
    const dateB = this.parseDate(b);
    if (!dateA || !dateB) return false;
    return isSameDay(dateA, dateB);
  }

  isPastDate(date: DateInput): boolean {
    const parsed = this.parseDate(date);
    if (!parsed) return false;
    return isPast(parsed);
  }

  isFutureDate(date: DateInput): boolean {
    const parsed = this.parseDate(date);
    if (!parsed) return false;
    return isFuture(parsed);
  }

  isTodayDate(date: DateInput): boolean {
    const parsed = this.parseDate(date);
    if (!parsed) return false;
    return isToday(parsed);
  }

  isDateWithinRange(date: DateInput, rangeStart: DateInput, rangeEnd: DateInput): boolean {
    const d = this.parseDate(date);
    const start = this.parseDate(rangeStart);
    const end = this.parseDate(rangeEnd);
    if (!d || !start || !end) return false;
    return isWithinRange(d, start, end);
  }

  // --- Arithmetic ---

  addMinutesToDate(date: DateInput, amount: number): Date {
    return addMinutes(new Date(date), amount);
  }

  addHoursToDate(date: DateInput, amount: number): Date {
    return addHours(new Date(date), amount);
  }

  addDaysToDate(date: DateInput, amount: number): Date {
    return addDays(new Date(date), amount);
  }

  addMonthsToDate(date: DateInput, amount: number): Date {
    return addMonths(new Date(date), amount);
  }

  subtractDays(date: DateInput, amount: number): Date {
    return subDays(new Date(date), amount);
  }

  subtractMonths(date: DateInput, amount: number): Date {
    return subMonths(new Date(date), amount);
  }

  getStartOfDay(date: DateInput): Date {
    return startOfDay(new Date(date));
  }

  getEndOfDay(date: DateInput): Date {
    return endOfDay(new Date(date));
  }

  getStartOfWeek(date: DateInput): Date {
    return startOfWeek(new Date(date));
  }

  getEndOfWeek(date: DateInput): Date {
    return endOfWeek(new Date(date));
  }

  // --- Difference ---

  getDifferenceInMinutes(a: DateInput, b: DateInput): number {
    return differenceInMinutes(new Date(a), new Date(b));
  }

  getDifferenceInHours(a: DateInput, b: DateInput): number {
    return differenceInHours(new Date(a), new Date(b));
  }

  getDifferenceInDays(a: DateInput, b: DateInput): number {
    return differenceInDays(new Date(a), new Date(b));
  }

  getDifferenceInCalendarDays(a: DateInput, b: DateInput): number {
    return differenceInCalendarDays(new Date(a), new Date(b));
  }

  getDifferenceInMonths(a: DateInput, b: DateInput): number {
    return differenceInMonths(new Date(a), new Date(b));
  }

  // --- UTC ---

  toUTC(date: DateInput): Date {
    const d = new Date(date);
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds()));
  }

  nowUTC(): Date {
    return new Date(Date.now());
  }

   private _isDateForamt(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  isTimestampFormat(str: string): boolean {
    // Check if the string is a valid timestamp (epoch time)
    const timestampRegex: RegExp = /^\d+$/;
  
    if (timestampRegex.test(str)) {
      const timestamp: number = Number(str);
      const tempDate: Date = new Date(timestamp);
  
      // Check if the timestamp is valid
      return !isNaN(timestamp) && this._isDateForamt(tempDate);
    }
  
    return false; // Not a valid timestamp format
  } 


  differenceInMonthsIncludingPartial(startDate, endDate) {
    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
      throw new Error('Invalid Date');
    }

    let monthsDifference = differenceInCalendarMonths(endDate, startDate);

    // Adjust for partial months
    const startDay = getDate(startDate);
    const endDay = getDate(endDate);

    if (endDay >= startDay) {
      monthsDifference += 1;
    }

    return monthsDifference;
  }

  // --- Additional arithmetic ---

  addSecondsToDate(date: DateInput, amount: number): Date {
    return addSeconds(new Date(date), amount);
  }

  addWeeksToDate(date: DateInput, amount: number): Date {
    return addWeeks(new Date(date), amount);
  }

  addYearsToDate(date: DateInput, amount: number): Date {
    return addYears(new Date(date), amount);
  }

  subtractMinutes(date: DateInput, amount: number): Date {
    return subMinutes(new Date(date), amount);
  }

  subtractWeeks(date: DateInput, amount: number): Date {
    return subWeeks(new Date(date), amount);
  }

  setSecondsToDate(date: DateInput, seconds: number): Date {
    return setSeconds(new Date(date), seconds);
  }

  setISOWeekNumber(date: DateInput, week: number): Date {
    return setISOWeek(new Date(date), week);
  }

  // --- Additional boundary helpers ---

  getStartOfMonth(date: DateInput): Date {
    return startOfMonth(new Date(date));
  }

  getEndOfMonth(date: DateInput): Date {
    return endOfMonth(new Date(date));
  }

  getStartOfYear(date: DateInput): Date {
    return startOfYear(new Date(date));
  }

  getStartOfQuarter(date: DateInput): Date {
    return startOfQuarter(new Date(date));
  }

  getEndOfQuarter(date: DateInput): Date {
    return endOfQuarter(new Date(date));
  }

  getLastDayOfMonth(date: DateInput): Date {
    return lastDayOfMonth(new Date(date));
  }

  // --- Additional differences ---

  getDifferenceInMilliseconds(a: DateInput, b: DateInput): number {
    return differenceInMilliseconds(new Date(a), new Date(b));
  }

  getDifferenceInWeeks(a: DateInput, b: DateInput): number {
    return differenceInWeeks(new Date(a), new Date(b));
  }

  getDifferenceInYears(a: DateInput, b: DateInput): number {
    return differenceInYears(new Date(a), new Date(b));
  }

  // --- Additional checks ---

  isYesterdayDate(date: DateInput): boolean {
    const parsed = this.parseDate(date);
    if (!parsed) return false;
    return isYesterday(parsed);
  }

  isTomorrowDate(date: DateInput): boolean {
    const parsed = this.parseDate(date);
    if (!parsed) return false;
    return isTomorrow(parsed);
  }

  isEqualDate(a: DateInput, b: DateInput): boolean {
    const dateA = this.parseDate(a);
    const dateB = this.parseDate(b);
    if (!dateA || !dateB) return false;
    return isEqual(dateA, dateB);
  }

  isDateObject(value: any): boolean {
    return isDate(value);
  }

  // --- Accessors ---

  getDayOfWeek(date: DateInput): number {
    return getDay(new Date(date));
  }

  getDayOfMonth(date: DateInput): number {
    return getDate(new Date(date));
  }

  getDaysInMonthCount(date: DateInput): number {
    return getDaysInMonth(new Date(date));
  }

  getISOWeekNumber(date: DateInput): number {
    return getISOWeek(new Date(date));
  }

  // --- Human-readable relative time ---

  distanceInWordsToNow(date: DateInput, options?: { includeSeconds?: boolean; addSuffix?: boolean }): string {
    const parsed = this.parseDate(date);
    if (!parsed) return '';
    return distanceInWordsToNow(parsed, options);
  }

  distanceInWordsBetween(dateA: DateInput, dateB: DateInput, options?: { includeSeconds?: boolean; addSuffix?: boolean }): string {
    const a = this.parseDate(dateA);
    const b = this.parseDate(dateB);
    if (!a || !b) return '';
    return distanceInWords(a, b, options);
  }

  // --- Sorting ---

  /** Comparator for sorting dates descending (newest first). Returns negative if a > b. */
  compareDateDesc(a: DateInput, b: DateInput): number {
    return compareDesc(new Date(a), new Date(b));
  }

  /** Returns the latest (maximum) date from an array */
  getMaxDate(dates: DateInput[]): Date {
    return max(...dates.map(d => new Date(d)));
  }

  // --- Parsing ---

  /** Parse a date string to a Date object */
  parseDateString(dateString: string): Date {
    return parse(dateString);
  }

  getDifferenceInSeconds(a: DateInput, b: DateInput): number {
    return differenceInSeconds(new Date(a), new Date(b));
  }

  isThisMonthDate(date: DateInput): boolean {
    return isThisMonth(new Date(date));
  }

  isThisYearDate(date: DateInput): boolean {
    return isThisYear(new Date(date));
  }

  isThisISOWeekDate(date: DateInput): boolean {
    return isThisISOWeek(new Date(date));
  }

  /** Formats a second count as zero-padded mm:ss, e.g. 83 → '01:23'. */
  formatSecondsToMinutes(totalSeconds: number): string {
    const seconds = Math.max(0, Math.floor(totalSeconds));
    const mm = Math.floor(seconds / 60).toString().padStart(2, '0');
    const ss = (seconds % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  }
}
